import { useState, useEffect, useRef } from 'react'
import { Calendar, Search, Eye, X, MapPin, ExternalLink, Settings, Save, Download, Clock, FileSpreadsheet, FileText, Filter, Sparkles, Users, Timer, AlertTriangle, Info, TrendingUp, ChevronDown } from 'lucide-react'
import { attendanceAPI, centersAPI, districtsAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { formatDate, formatTime, formatDateTime } from '../../lib/dateFormat'
import { parsePaginatedResponse } from '../../lib/pagination'
import Pagination from '../../components/Pagination'

/** Backend stores total_hours as decimal (e.g. 8.32) — convert to H:mm
 * (e.g. "8:19") for display, since "8.32 hours" reads as minutes to most
 * people at a glance. */
function formatDurationHHMM(decimalHours: number | null | undefined): string {
  if (decimalHours == null) return '—'
  const totalMinutes = Math.round(decimalHours * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

const STATUS_STYLES: Record<string, string> = {
  PRESENT: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  LATE: 'bg-orange-50 text-orange-700 ring-orange-200',
  HALF_DAY: 'bg-amber-50 text-amber-700 ring-amber-200',
  ABSENT: 'bg-red-50 text-red-700 ring-red-200',
  ON_LEAVE: 'bg-blue-50 text-blue-700 ring-blue-200',
  HOLIDAY: 'bg-purple-50 text-purple-700 ring-purple-200',
  WEEK_OFF: 'bg-slate-100 text-slate-700 ring-slate-200',
  MISSED_CHECK_OUT: 'bg-rose-50 text-rose-700 ring-rose-200',
  EARLY_LEAVE: 'bg-yellow-50 text-yellow-800 ring-yellow-200',
}

const STATUS_DOTS: Record<string, string> = {
  PRESENT: 'bg-emerald-500',
  LATE: 'bg-orange-500',
  HALF_DAY: 'bg-amber-500',
  ABSENT: 'bg-red-500',
  ON_LEAVE: 'bg-blue-500',
  HOLIDAY: 'bg-purple-500',
  WEEK_OFF: 'bg-slate-500',
  MISSED_CHECK_OUT: 'bg-rose-500',
  EARLY_LEAVE: 'bg-yellow-500',
}

const STATUS_LABELS: Record<string, string> = {
  PRESENT: 'Present',
  LATE: 'Late',
  HALF_DAY: 'Half Day',
  ABSENT: 'Absent',
  ON_LEAVE: 'On Leave',
  HOLIDAY: 'Holiday',
  WEEK_OFF: 'Week Off',
  MISSED_CHECK_OUT: 'Missed Check-Out',
  EARLY_LEAVE: 'Early Leave',
}

const ALL_STATUSES = Object.keys(STATUS_LABELS)

// Values match the backend query params (apps/attendance/query_filters.py).
const GEOFENCE_OPTIONS = [
  { value: 'check_in_outside', label: 'Check-in outside geofence' },
  { value: 'check_out_outside', label: 'Check-out outside geofence' },
  { value: 'any_outside', label: 'Check-in or check-out outside' },
  { value: 'inside', label: 'Within geofence' },
]
const PUNCTUALITY_OPTIONS = [
  { value: 'late', label: 'Late arrival' },
  { value: 'early_exit', label: 'Early exit' },
  { value: 'late_or_early', label: 'Late or early exit' },
]
const CHECKOUT_OPTIONS = [
  { value: 'missing', label: 'Check-out missing' },
  { value: 'done', label: 'Checked out' },
]
const EDITED_OPTIONS = [
  { value: 'yes', label: 'Edited by admin' },
  { value: 'no', label: 'Not edited' },
]

const selectClass =
  'w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 text-sm focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all'

/** Multi-select dropdown for attendance status (checkbox list in a popover). */
function StatusMultiSelect({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const toggle = (s: string) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s])
  const label =
    value.length === 0 ? 'All statuses' : value.length === 1 ? STATUS_LABELS[value[0]] : `${value.length} statuses`

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${selectClass} flex items-center justify-between text-left`}
      >
        <span className={value.length ? 'text-neutral-900' : 'text-neutral-500'}>{label}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[210px] bg-white rounded-xl shadow-lg ring-1 ring-neutral-200 p-1.5">
          {ALL_STATUSES.map((s) => (
            <label
              key={s}
              className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm text-neutral-700 hover:bg-neutral-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={value.includes(s)}
                onChange={() => toggle(s)}
                className="rounded border-neutral-300 text-[#E4670F] focus:ring-[#E4670F]"
              />
              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[s]}`} />
              {STATUS_LABELS[s]}
            </label>
          ))}
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full mt-1 px-2.5 py-1.5 text-xs font-medium text-neutral-500 hover:text-[#C24A0A] text-left border-t border-neutral-100"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default function AttendanceAdminPage() {
  const { success, error: toastError } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [statuses, setStatuses] = useState<string[]>([])
  const [geofence, setGeofence] = useState('')
  const [punctuality, setPunctuality] = useState('')
  const [checkOutFilter, setCheckOutFilter] = useState('')
  const [editedFilter, setEditedFilter] = useState('')
  const [centreId, setCentreId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const [centres, setCentres] = useState<any[]>([])
  const [districts, setDistricts] = useState<any[]>([])
  const [attendance, setAttendance] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)

  const [selected, setSelected] = useState<any | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState<any>({})
  const [isSaving, setIsSaving] = useState(false)

  const [showShiftModal, setShowShiftModal] = useState(false)
  const [shiftConfig, setShiftConfig] = useState<any>(null)
  const [shiftForm, setShiftForm] = useState({ shift_start_time: '', shift_end_time: '', grace_period_minutes: '' })
  const [isSavingShift, setIsSavingShift] = useState(false)
  const [shiftErrors, setShiftErrors] = useState({ shift_start_time: '', shift_end_time: '', grace_period_minutes: '' })
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  /** The ONE source of the applied filters. The table and both downloads use it,
   * so an XLSX / PDF always contains exactly the records matching the screen. */
  const buildFilterParams = () => {
    const p: Record<string, string> = {}
    if (dateFrom) p.from = dateFrom
    if (dateTo) p.to = dateTo
    if (searchTerm.trim()) p.search = searchTerm.trim()
    if (statuses.length) p.status = statuses.join(',')
    if (geofence) p.geofence = geofence
    if (punctuality) p.punctuality = punctuality
    if (checkOutFilter) p.check_out = checkOutFilter
    if (editedFilter) p.edited = editedFilter
    if (districtId) p.district = districtId
    if (centreId) p.training_centre = centreId
    return p
  }
  const activeFilterCount = Object.keys(buildFilterParams()).length

  /** Any filter change goes back to page 1 (page N may not exist in the new result set). */
  const withReset = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v)
    setPage(1)
  }

  const clearAllFilters = () => {
    setSearchTerm(''); setDateFrom(''); setDateTo(''); setStatuses([]); setGeofence('')
    setPunctuality(''); setCheckOutFilter(''); setEditedFilter(''); setCentreId(''); setDistrictId('')
    setPage(1)
  }

  const loadAttendance = async (pageArg = page) => {
    setIsLoading(true)
    try {
      const res = await attendanceAPI.getAllAttendance({ page: pageArg, ...buildFilterParams() })
      const parsed = parsePaginatedResponse(res.data)
      setAttendance(parsed.results)
      setCount(parsed.count)
      setHasNext(parsed.hasNext)
      setHasPrev(parsed.hasPrev)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load attendance records.'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleExport = async (file_format: 'xlsx' | 'pdf') => {
    setIsExporting(true)
    try {
      const res = await attendanceAPI.export({ file_format, ...buildFilterParams() })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      const stamp = new Date().toISOString().slice(0, 10)
      link.setAttribute('download', `IRCS_attendance_${stamp}.${file_format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      success(`Downloaded ${count} record${count === 1 ? '' : 's'} matching the current filters.`)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to export attendance records.'))
    } finally {
      setIsExporting(false)
    }
  }

  // Filter dropdown data (training centres / districts)
  useEffect(() => {
    centersAPI.listAll().then((r) => setCentres(r.data.results || r.data)).catch(() => setCentres([]))
    districtsAPI.listAll().then((r) => setDistricts(r.data.results || r.data)).catch(() => setDistricts([]))
  }, [])

  useEffect(() => {
    loadAttendance(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dateFrom, dateTo, statuses.join(','), geofence, punctuality, checkOutFilter, editedFilter, centreId, districtId])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1)
      loadAttendance(1)
    }, 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const filtered = attendance

  const openDetails = (rec: any) => {
    setSelected(rec)
    setEditMode(false)
    setEditForm({
      check_in_time: rec.check_in_time ? toLocalInput(rec.check_in_time) : '',
      check_out_time: rec.check_out_time ? toLocalInput(rec.check_out_time) : '',
      status: rec.status,
      admin_remarks: rec.admin_remarks || '',
    })
  }

  function toLocalInput(iso: string) {
    const d = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const handleSaveEdit = async () => {
    if (!selected) return
    if (!editForm.admin_remarks?.trim()) {
      toastError('A remark is required explaining this manual edit.')
      return
    }
    setIsSaving(true)
    try {
      const payload: any = {
        status: editForm.status,
        admin_remarks: editForm.admin_remarks,
      }
      if (editForm.check_in_time) payload.check_in_time = new Date(editForm.check_in_time).toISOString()
      if (editForm.check_out_time) payload.check_out_time = new Date(editForm.check_out_time).toISOString()

      const res = await attendanceAPI.adminEdit(selected.id, payload)
      success('Attendance record updated.')
      setSelected(res.data)
      setEditMode(false)
      await loadAttendance()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to update attendance record.'))
    } finally {
      setIsSaving(false)
    }
  }

  const mapLink = (lat: string | number, lng: string | number) =>
    `https://www.google.com/maps?q=${lat},${lng}`

  const openShiftSettings = async () => {
    setShowShiftModal(true)
    setShiftErrors({ shift_start_time: '', shift_end_time: '', grace_period_minutes: '' })

    try {
      const res = await attendanceAPI.getShiftConfig()
      setShiftConfig(res.data)

      const hasConfig = res.data &&
        (res.data.shift_start_time || res.data.shift_end_time ||
         (res.data.grace_period_minutes !== null && res.data.grace_period_minutes !== undefined))

      if (hasConfig) {
        setShiftForm({
          shift_start_time: res.data.shift_start_time || '',
          shift_end_time: res.data.shift_end_time || '',
          grace_period_minutes: res.data.grace_period_minutes !== null && res.data.grace_period_minutes !== undefined
            ? String(res.data.grace_period_minutes)
            : '',
        })
        setHasExistingConfig(true)
      } else {
        setShiftForm({
          shift_start_time: '',
          shift_end_time: '',
          grace_period_minutes: '',
        })
        setHasExistingConfig(false)
      }
    } catch (err: any) {
      setShiftForm({
        shift_start_time: '',
        shift_end_time: '',
        grace_period_minutes: '',
      })
      setHasExistingConfig(false)
    }
  }

  const validateShiftForm = () => {
    const errors = { shift_start_time: '', shift_end_time: '', grace_period_minutes: '' }
    let isValid = true

    if (!shiftForm.shift_start_time || shiftForm.shift_start_time.trim() === '') {
      errors.shift_start_time = 'Shift start time is required.'
      isValid = false
    }

    if (!shiftForm.shift_end_time || shiftForm.shift_end_time.trim() === '') {
      errors.shift_end_time = 'Shift end time is required.'
      isValid = false
    }

    if (!shiftForm.grace_period_minutes || shiftForm.grace_period_minutes.trim() === '') {
      errors.grace_period_minutes = 'Grace period is required.'
      isValid = false
    } else if (Number(shiftForm.grace_period_minutes) < 0) {
      errors.grace_period_minutes = 'Grace period cannot be negative.'
      isValid = false
    }

    setShiftErrors(errors)
    return isValid
  }

  const handleSaveShift = async () => {
    if (!validateShiftForm()) return

    setIsSavingShift(true)
    try {
      await attendanceAPI.updateShiftConfig({
        shift_start_time: shiftForm.shift_start_time,
        shift_end_time: shiftForm.shift_end_time,
        grace_period_minutes: Number(shiftForm.grace_period_minutes),
      })
      success('Shift configuration updated. New check-ins will use this cut-off.')
      setShowShiftModal(false)
      setShiftErrors({ shift_start_time: '', shift_end_time: '', grace_period_minutes: '' })
      setHasExistingConfig(true)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to update shift configuration.'))
    } finally {
      setIsSavingShift(false)
    }
  }

  const inputClass = "w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-7">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Time Tracking
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Attendance Records
          </h1>
          <p className="text-sm text-neutral-500">View and manage employee attendance</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleExport('xlsx')}
            disabled={isExporting || isLoading || count === 0}
            title={count === 0 ? 'No records to download' : `Download ${count} record${count === 1 ? '' : 's'} matching the current filters`}
            className="group flex items-center gap-2 px-4 py-2.5 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            <FileSpreadsheet className="w-4 h-4 transition-transform group-hover:scale-110" />
            XLSX
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isExporting || isLoading || count === 0}
            title={count === 0 ? 'No records to download' : `Download ${count} record${count === 1 ? '' : 's'} matching the current filters`}
            className="group flex items-center gap-2 px-4 py-2.5 border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            <FileText className="w-4 h-4 transition-transform group-hover:scale-110" />
            PDF
          </button>
          <button
            onClick={openShiftSettings}
            className="group flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5"
          >
            <Settings className="w-4 h-4 transition-transform group-hover:rotate-90 duration-300" />
            Shift Config
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center">
            <Filter className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-sm font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
            Filters
          </h2>
          {activeFilterCount > 0 && (
            <span className="text-[11px] font-semibold text-[#C24A0A] bg-[#E4670F]/10 rounded-full px-2 py-0.5">
              {activeFilterCount} active
            </span>
          )}
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="ml-auto text-xs font-medium text-neutral-500 hover:text-[#C24A0A] transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Search Employee</label>
            <div className="relative group">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
              <input
                type="text"
                placeholder="Name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">From Date</label>
            <div className="relative group">
              <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => withReset(setDateFrom)(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">To Date</label>
            <div className="relative group">
              <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => withReset(setDateTo)(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Status</label>
            <StatusMultiSelect value={statuses} onChange={withReset(setStatuses)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Geofence</label>
            <select value={geofence} onChange={(e) => withReset(setGeofence)(e.target.value)} className={selectClass}>
              <option value="">All</option>
              {GEOFENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Punctuality</label>
            <select value={punctuality} onChange={(e) => withReset(setPunctuality)(e.target.value)} className={selectClass}>
              <option value="">All</option>
              {PUNCTUALITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Check-out</label>
            <select value={checkOutFilter} onChange={(e) => withReset(setCheckOutFilter)(e.target.value)} className={selectClass}>
              <option value="">All</option>
              {CHECKOUT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Manual edits</label>
            <select value={editedFilter} onChange={(e) => withReset(setEditedFilter)(e.target.value)} className={selectClass}>
              <option value="">All</option>
              {EDITED_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">District</label>
            <select
              value={districtId}
              onChange={(e) => {
                setDistrictId(e.target.value)
                setCentreId('') // a centre from another district would give an empty result
                setPage(1)
              }}
              className={selectClass}
            >
              <option value="">All districts</option>
              {districts.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Training Centre</label>
            <select value={centreId} onChange={(e) => withReset(setCentreId)(e.target.value)} className={selectClass}>
              <option value="">All centres</option>
              {centres
                .filter((c: any) => !districtId || c.district == null || String(c.district) === String(districtId))
                .map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Results strip ── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Users className="w-4 h-4 text-neutral-400" />
          <span>
            <span className="font-semibold text-neutral-900 tabular-nums">{count}</span>{' '}
            {count === 1 ? 'record' : 'records'}
            {activeFilterCount > 0 && <span className="text-neutral-400"> match the filters — XLSX / PDF download these</span>}
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50/80 border-b border-neutral-200/70">
              <tr>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Sr.</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Employee</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Date</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Check-in</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Check-out</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Total Hours</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3.5 text-right text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
                      <p className="text-sm text-neutral-500">Loading attendance...</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center">
                        <Calendar className="w-7 h-7 text-neutral-400" />
                      </div>
                      <p className="text-sm font-medium text-neutral-700">No attendance records found</p>
                      <p className="text-xs text-neutral-400">Try adjusting your filters or date range.</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map((rec, index) => (
                <tr
                  key={rec.id}
                  className="border-b border-neutral-100 last:border-b-0 hover:bg-[#FAF6EE]/60 transition-colors"
                >
                  <td className="px-5 py-4 text-sm text-neutral-500 tabular-nums">
                    {(page - 1) * 10 + index + 1}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-xs font-bold text-white ring-2 ring-white shadow-sm flex-shrink-0">
                        {rec.employee_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 truncate max-w-[160px]">
                          {rec.employee_name}
                        </p>
                        <p className="text-xs text-neutral-500 tabular-nums">{rec.employee_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-neutral-700">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-neutral-400" />
                      {formatDate(rec.date)}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-neutral-700 tabular-nums">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      {formatTime(rec.check_in_time)}
                      {rec.is_outside_geofence && (
                        <span
                          title={`Checked in outside the geofence${rec.check_in_distance_m != null ? ` (${rec.check_in_distance_m} m)` : ''}`}
                          className="inline-flex"
                        >
                          <MapPin className="w-3.5 h-3.5 text-orange-500" />
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-neutral-700 tabular-nums">
                      <span className={`w-1.5 h-1.5 rounded-full ${rec.check_out_time ? 'bg-red-500' : 'bg-neutral-300'}`} />
                      {formatTime(rec.check_out_time)}
                      {rec.is_check_out_outside_geofence && (
                        <span
                          title={`Checked out outside the geofence${rec.check_out_distance_m != null ? ` (${rec.check_out_distance_m} m)` : ''}`}
                          className="inline-flex"
                        >
                          <MapPin className="w-3.5 h-3.5 text-orange-500" />
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-neutral-900 tabular-nums">
                      <Timer className="w-3.5 h-3.5 text-neutral-400" />
                      {formatDurationHHMM(rec.total_hours)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 ${STATUS_STYLES[rec.status] || 'bg-neutral-100 text-neutral-700 ring-neutral-200'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[rec.status] || 'bg-neutral-400'}`} />
                        {STATUS_LABELS[rec.status] || rec.status}
                      </span>
                      {rec.admin_remarks && (
                        <span
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700"
                          title={`Edited: ${rec.admin_remarks}`}
                        >
                          <Info className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => openDetails(rec)}
                      className="group inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-lg transition-all"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} onPageChange={setPage} count={count} hasNext={hasNext} hasPrev={hasPrev} />

      {/* ── Details / Admin Edit Modal ── */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30 flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-neutral-900 truncate" style={{ fontFamily: 'Georgia, serif' }}>
                    {selected.employee_name}
                  </h2>
                  <p className="text-xs text-neutral-500">{formatDate(selected.date)}</p>
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Check-in */}
                <div className="border border-neutral-200 rounded-2xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-emerald-50/60 border-b border-emerald-100 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <h3 className="text-sm font-bold text-emerald-900">Check-in</h3>
                  </div>
                  <div className="p-4">
                    {selected.check_in_photo ? (
                      <img src={selected.check_in_photo} alt="Check-in selfie" className="w-full h-48 object-cover rounded-xl mb-3 ring-1 ring-neutral-200" />
                    ) : (
                      <div className="w-full h-48 flex flex-col items-center justify-center bg-neutral-50 rounded-xl mb-3 text-neutral-400 text-sm border border-dashed border-neutral-200">
                        <Clock className="w-6 h-6 mb-1 text-neutral-300" />
                        No photo
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                      <Clock className="w-4 h-4 text-neutral-400" />
                      <span className="font-medium tabular-nums">{formatDateTime(selected.check_in_time)}</span>
                    </div>
                    {selected.check_in_lat && (
                      <a
                        href={mapLink(selected.check_in_lat, selected.check_in_lng)}
                        target="_blank" rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#C24A0A] hover:text-[#7A1F1F] hover:underline font-medium"
                      >
                        <MapPin className="w-4 h-4" /> View on map <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {selected.is_outside_geofence && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                          Checked in outside the geofence
                          {selected.check_in_distance_m != null ? ` (${selected.check_in_distance_m} m away).` : '.'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Check-out */}
                <div className="border border-neutral-200 rounded-2xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-red-50/60 border-b border-red-100 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <h3 className="text-sm font-bold text-red-900">Check-out</h3>
                  </div>
                  <div className="p-4">
                    {selected.check_out_photo ? (
                      <img src={selected.check_out_photo} alt="Check-out selfie" className="w-full h-48 object-cover rounded-xl mb-3 ring-1 ring-neutral-200" />
                    ) : (
                      <div className="w-full h-48 flex flex-col items-center justify-center bg-neutral-50 rounded-xl mb-3 text-neutral-400 text-sm border border-dashed border-neutral-200">
                        <Clock className="w-6 h-6 mb-1 text-neutral-300" />
                        {selected.check_in_time ? 'Not checked out yet' : 'No photo'}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-neutral-700">
                      <Clock className="w-4 h-4 text-neutral-400" />
                      <span className="font-medium tabular-nums">{formatDateTime(selected.check_out_time)}</span>
                    </div>
                    {selected.check_out_lat && (
                      <a
                        href={mapLink(selected.check_out_lat, selected.check_out_lng)}
                        target="_blank" rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#C24A0A] hover:text-[#7A1F1F] hover:underline font-medium"
                      >
                        <MapPin className="w-4 h-4" /> View on map <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {selected.is_check_out_outside_geofence && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                          Checked out outside the geofence
                          {selected.check_out_distance_m != null ? ` (${selected.check_out_distance_m} m away).` : '.'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-xl px-4 py-2.5">
                <MapPin className="w-4 h-4 text-neutral-400" />
                Training Centre:{' '}
                <span className="font-medium text-neutral-800">
                  {selected.training_centre_name || '—'}
                  {selected.training_centre_code ? ` (${selected.training_centre_code})` : ''}
                </span>
              </div>

              {selected.admin_remarks && !editMode && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Info className="w-4 h-4 text-amber-700" />
                    <p className="text-sm font-semibold text-amber-900">Admin remark</p>
                  </div>
                  <p className="text-sm text-amber-800">{selected.admin_remarks}</p>
                  {selected.admin_edited_by_name && (
                    <p className="text-xs text-amber-600 mt-1.5">
                      Edited by {selected.admin_edited_by_name} on {formatDateTime(selected.admin_edited_at)}
                    </p>
                  )}
                </div>
              )}

              {/* Admin edit */}
              <div className="mt-6 border-t border-neutral-100 pt-5">
                {!editMode ? (
                  <button
                    onClick={() => setEditMode(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#E4670F]/30 bg-[#E4670F]/[0.04] text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-xl font-medium text-sm transition-all"
                  >
                    <Settings className="w-4 h-4" />
                    Fix missed check-in / check-out (admin only)
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3.5 py-2.5 text-xs text-blue-800">
                      <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>
                        Only admins can edit attendance times. A remark is required so the change is auditable.
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Check-in time</label>
                        <input
                          type="datetime-local"
                          value={editForm.check_in_time}
                          onChange={(e) => setEditForm({ ...editForm, check_in_time: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Check-out time</label>
                        <input
                          type="datetime-local"
                          value={editForm.check_out_time}
                          onChange={(e) => setEditForm({ ...editForm, check_out_time: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Status</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className={inputClass}
                      >
                        {ALL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                        Remark <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={editForm.admin_remarks}
                        onChange={(e) => setEditForm({ ...editForm, admin_remarks: e.target.value })}
                        placeholder="e.g. Employee forgot to check out — confirmed with them by phone."
                        rows={2}
                        className={inputClass}
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setEditMode(false)}
                        className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-sm text-neutral-700 hover:bg-neutral-50 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium text-sm disabled:opacity-50 transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
                      >
                        {isSaving ? 'Saving...' : 'Save Correction'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Shift Configuration Modal ── */}
      {showShiftModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  <Settings className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  Shift Configuration
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowShiftModal(false)
                  setShiftErrors({ shift_start_time: '', shift_end_time: '', grace_period_minutes: '' })
                }}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3.5 py-2.5 text-xs text-blue-800 mb-5">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  Employees checking in after the shift start time + grace period are marked <strong>LATE</strong> automatically.
                </span>
              </div>

              <div className="space-y-4">
                {/* Shift Start */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                    Shift Start Time <span className="text-red-500">*</span>
                  </label>
                  <div className="relative group">
                    <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                    <input
                      type="time"
                      value={shiftForm.shift_start_time}
                      onChange={(e) => {
                        setShiftForm({ ...shiftForm, shift_start_time: e.target.value })
                        if (shiftErrors.shift_start_time) setShiftErrors({ ...shiftErrors, shift_start_time: '' })
                      }}
                      className={`w-full pl-10 pr-4 py-2.5 bg-neutral-50 border rounded-xl text-neutral-900 focus:bg-white focus:ring-4 outline-none transition-all ${
                        shiftErrors.shift_start_time
                          ? 'border-red-400 focus:ring-red-500/10 focus:border-red-500'
                          : 'border-neutral-200 focus:ring-[#E4670F]/10 focus:border-[#E4670F]'
                      }`}
                    />
                  </div>
                  {shiftErrors.shift_start_time && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {shiftErrors.shift_start_time}
                    </p>
                  )}
                </div>

                {/* Shift End */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                    Shift End Time <span className="text-red-500">*</span>
                  </label>
                  <div className="relative group">
                    <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                    <input
                      type="time"
                      value={shiftForm.shift_end_time}
                      onChange={(e) => {
                        setShiftForm({ ...shiftForm, shift_end_time: e.target.value })
                        if (shiftErrors.shift_end_time) setShiftErrors({ ...shiftErrors, shift_end_time: '' })
                      }}
                      className={`w-full pl-10 pr-4 py-2.5 bg-neutral-50 border rounded-xl text-neutral-900 focus:bg-white focus:ring-4 outline-none transition-all ${
                        shiftErrors.shift_end_time
                          ? 'border-red-400 focus:ring-red-500/10 focus:border-red-500'
                          : 'border-neutral-200 focus:ring-[#E4670F]/10 focus:border-[#E4670F]'
                      }`}
                    />
                  </div>
                  {shiftErrors.shift_end_time && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {shiftErrors.shift_end_time}
                    </p>
                  )}
                </div>

                {/* Grace Period */}
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                    Grace Period (minutes) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative group">
                    <Timer className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                    <input
                      type="number"
                      min="0"
                      value={shiftForm.grace_period_minutes}
                      onChange={(e) => {
                        setShiftForm({ ...shiftForm, grace_period_minutes: e.target.value })
                        if (shiftErrors.grace_period_minutes) setShiftErrors({ ...shiftErrors, grace_period_minutes: '' })
                      }}
                      className={`w-full pl-10 pr-4 py-2.5 bg-neutral-50 border rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 outline-none transition-all tabular-nums ${
                        shiftErrors.grace_period_minutes
                          ? 'border-red-400 focus:ring-red-500/10 focus:border-red-500'
                          : 'border-neutral-200 focus:ring-[#E4670F]/10 focus:border-[#E4670F]'
                      }`}
                      placeholder="Enter grace period in minutes"
                    />
                  </div>
                  {shiftErrors.grace_period_minutes && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {shiftErrors.grace_period_minutes}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => {
                  setShowShiftModal(false)
                  setShiftErrors({ shift_start_time: '', shift_end_time: '', grace_period_minutes: '' })
                }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveShift}
                disabled={isSavingShift}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
              >
                <Save className="w-4 h-4" />
                {isSavingShift ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}